import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Select,
  MenuItem,
  Box,
  TablePagination,
} from "@mui/material";
import { httpClient } from "../../axios";

const UserEmployeeTable = () => {
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingTasks, setPendingTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [query, setquery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [newStatus, setNewStatus] = useState("");

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Fetch data on component mount or when page/rowsPerPage changes
  useEffect(() => {
    fetchEmployees();
  }, [page, rowsPerPage]);

  const fetchEmployees = async (searchQuery = "") => {
    try {
      const endpoint = searchQuery
        ? `/employee/filter?name=${searchQuery}`
        : "/employee/getAll";

      const response = await httpClient.get(endpoint);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
      setData([]);
    }
  };

  const handleSearchClick = () => {
    setPage(0); // Reset to first page when searching
    fetchEmployees(searchQuery);
  };

  const fetchPendingTasks = async () => {
    try {
      const response = await httpClient.get("/api/filter?taskStatus=pending");
      setPendingTasks(response.data);
    } catch (error) {
      console.error("Error fetching pending tasks:", error);
    }
  };

  const fetchFilteredTasks = async () => {
    if (!startDate || !endDate) return;
    try {
      const response = await httpClient.get(`/api/tasks/employee/date-filter${startDate}&endDate=${endDate}`
      );
      setFilteredTasks(response.data);
    } catch (error) {
      console.error("Error fetching filtered tasks:", error);
    }
  };

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    if (!query) {
      fetchEmployees();
    }
  };

  useEffect(() => {
    console.log("selectedEmployee", selectedEmployee);
  }, [selectedEmployee]);

  const fetchData = async () => {
    try {
      const response = await httpClient.get("employee/getAll");
      setData(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleConfirmOpen = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setDeleteId(null);
  };

  const handleOpen = (employee) => {
    setSelectedEmployee({ ...employee });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedEmployee(null);
  };

  const handleUpdate = async () => {
    const shallowCopy = { ...selectedEmployee };
    delete shallowCopy.tasks;

    try {
      await httpClient.put(`/employee/update`, shallowCopy);
      fetchData();
      handleClose();
    } catch (error) {
      console.error("Error updating employee:", error);
    }
  };

  const handleChange = (e) => {
    setSelectedEmployee({ ...selectedEmployee, [e.target.name]: e.target.value });
  };

  const deleteEmpData = async () => {
    try {
      await httpClient.delete(`/employee/delete/${deleteId}`);
      setData(data.filter((item) => item.id !== deleteId));
      handleConfirmClose();
    } catch (error) {
      console.error("Error deleting employee:", error);
    }
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate the current items to display
  const currentData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <TextField
          label="Search Employee"
          variant="outlined"
          fullWidth
          value={searchQuery}
          onChange={handleSearchChange}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSearchClick}
          style={{ height: "56px" }}
        >
          Search
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>ID</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Profile</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Address</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Phone No</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Department</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentData.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.id}</TableCell>
                <TableCell>
                  {item.profilePhotoPath ? (
                    <Avatar src={item.profilePhotoPath} alt={item.name} />
                  ) : (
                    <Avatar>{item.name?.charAt(0)}</Avatar>
                  )}
                </TableCell>
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.status || "N/A"}</TableCell>
                <TableCell>{item.address || "N/A"}</TableCell>
                <TableCell>{item.phoneNo || "N/A"}</TableCell>
                <TableCell>{item.email}</TableCell>
                <TableCell>{item.department || "N/A"}</TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleOpen(item)}
                    style={{ marginRight: "8px" }}
                  >
                    Update
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => handleConfirmOpen(item.id)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Update Employee Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Update Employee</DialogTitle>
        <DialogContent>
          {selectedEmployee && (
            <>
              <TextField
                margin="dense"
                label="Name"
                name="name"
                fullWidth
                value={selectedEmployee.name}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                label="Address"
                name="address"
                fullWidth
                value={selectedEmployee.address}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                label="Phone No"
                name="phoneNo"
                fullWidth
                value={selectedEmployee.phoneNo}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                label="Email"
                name="email"
                fullWidth
                value={selectedEmployee.email}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                label="Department"
                name="department"
                fullWidth
                value={selectedEmployee.department}
                onChange={handleChange}
              />
            </>
          )}

          <Typography> status:</Typography>
          <Select
            margin="dense"
            fullWidth
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
          >
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Inactive">Inactive</MenuItem>
          </Select>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleUpdate} color="primary">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmOpen} onClose={handleConfirmClose}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this employee?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={deleteEmpData} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserEmployeeTable;